import withApi from "./utils/apiPathFactory";

export default withApi({
  routes: {
    activityFeed: {
      base: "/activity-feed",
    },
    actionTemplates: {
      base: "/action-templates",
      show: "/action-templates/:id",
    },
    admin: {
      users: {
        base: "/admin/users",
        show: "/admin/users/:id",
        impersonations: {
          base: "/admin/users/impersonations",
          show: "/admin/users/:userId/impersonations",
        },
        automations: {
          base: "/admin/users/:userId/automations",
          validate: "/admin/users/:userId/automations/validate",
        },
      },
      companies: {
        base: "/admin/companies",
        show: "/admin/companies/:id",
        importUsers: "/admin/companies/:id/import-users",
        emailConfiguration: {
          index: "/admin/companies/:companyId/email-configuration",
        },
      },
    },
    autocomplete: {
      locations: {
        index: "/autocompletes/locations",
        show: "/autocompletes/locations/:id",
      },
      timezones: {
        show: "/autocompletes/timezones/:id",
      },
    },
    companies: {
      show: "/companies/:id",
      currentAdmin: "/companies/current/admin",
    },
    companyValues: {
      base: "/company-values",
      show: "/company-values/:id",
    },
    ssoProvider: {
      base: "/sso-provider",
    },
    dashboard: {
      home: "/dashboard/home",
      peopleTeamStats: "/dashboard/people-team-stats",
      calendar: "/dashboard/calendar",
      timeline: "/dashboard/timeline",
      starting: "/dashboard/starting",
    },
    peopleTeam: {
      taskGrid: {
        base: "/people-team/task-grid",
        preferences: "/people-team/task-grid/preferences",
      },
    },
    departments: {
      base: "/departments",
    },
    groups: {
      base: "/groups",
      show: "/groups/:id",
    },
    journeyPreviews: {
      base: "/journey-previews",
    },
    feedback: "/feedback",
    forms: {
      index: "/forms",
      show: "/forms/:id",
    },
    customForms: {
      base: "/custom-forms",
      show: "/custom-forms/:id",
      impact: "/custom-forms/:id/impact",
      fields: "/custom-forms/:id/fields",
      field: "/custom-forms/:id/fields/:fieldId",
      fieldsOrder: "/custom-forms/:id/fields/order",
      responses: "/custom-forms/:id/responses",
      responsesCsv: "/custom-forms/:id/responses.csv",
    },
    customFormResponses: {
      base: "/custom-form-responses",
      show: "/custom-form-responses/:id",
    },
    taskCustomFormResponse: "/tasks/:taskId/custom-form-response",
    integrations: {
      index: "/integrations",
      show: "/integrations/:id",
      available: {
        index: "integrations/available",
      },
    },
    messageServices: {
      base: "/message-services",
      show: "/message-services/:id",
      connections: {
        base: "/message-services/connections",
        show: "/message-services/connections/:id",
      },
      messages: {
        base: "/message-services/messages",
      },
      channels: {
        base: "/message-services/channels",
        show: "/message-services/channels/:id",
        sync: "/message-services/channels/sync",
      },
      users: {
        sync: "/message-services/users/sync",
      },
    },
    notifiers: {
      base: "/notifiers",
      show: "/notifiers/:id",
    },
    notifierTargets: {
      base: "/notifier-targets",
      show: "/notifier-targets/:id",
    },
    taskNotificationChannelOverrides: {
      base: "/task-notification-channel-overrides",
      show: "/task-notification-channel-overrides/:id",
    },
    onboardingJourneyNetworkRelations: {
      base: "/onboarding-journey-network-relations",
      show: "/onboarding-journey-network-relations/:id",
    },
    sessions: {
      authenticationOptions: "/sessions/authentication-options",
      magicLinks: {
        base: "/sessions/magic-links",
      },
      passwords: {
        base: "/sessions/passwords",
      },
      show: "/sessions/:id",
    },
    stages: {
      base: "/stages",
      show: "/stages/:id",
    },
    templateVariables: {
      base: "/template-variables",
      userMention: "/template-variables/user-mention",
    },
    users: {
      base: "/users",
      search: "/users/search",
      show: "/users/:id",
      deactivate: "/users/:id/deactivate",
      updateRoles: "/users/:id/update-roles",
    },
    emails: {
      base: "/emails",
      show: "/emails/:id",
      issues: "/emails/:id/issues",
    },
    emailTemplates: {
      base: "/email-templates",
      show: "/email-templates/:id",
      duplicate: "/email-templates/:id/duplicate",
    },
    issues: {
      base: "/issues",
      show: "/issues/:id",
      actions: "/issues/:id/actions",
      count: "/issues/count",
    },
    pendingIssues: {
      base: "/pending-issues",
    },
    meetings: {
      base: "/meetings",
      show: "/meetings/:id",
      issues: "/meetings/:id/issues",
    },
    meetingTemplates: {
      base: "/meeting-templates",
      show: "/meeting-templates/:id",
      duplicate: "/meeting-templates/:id/duplicate",
    },
    messages: {
      base: "/messages",
      show: "/messages/:id",
      issues: "/messages/:id/issues",
    },
    messageTemplates: {
      base: "/message-templates",
      show: "/message-templates/:id",
      duplicate: "/message-templates/:id/duplicate",
    },
    newHireJourneys: {
      base: "/new-hire-journeys",
      show: "/new-hire-journeys/:id",
      attentionStatuses: "/new-hire-journeys/attention-statuses",
      actions: "/new-hire-journeys/:id/actions",
      issues: "/new-hire-journeys/:id/issues",
      pendingIssues: "/new-hire-journeys/:id/pending-issues",
      tasks: "/new-hire-journeys/:id/tasks",
      emails: "/new-hire-journeys/:id/emails",
      messages: "/new-hire-journeys/:id/messages",
      meetings: "/new-hire-journeys/:id/meetings",
      checkpoints: "/new-hire-journeys/:id/checkpoints",
      close: "/new-hire-journeys/:id/close",
      skipRole: "/new-hire-journeys/:id/skip-role",
      paths: {
        base: "/new-hire-journeys/:id/paths",
      },
      withActiveIssues: {
        base: "/new-hire-journeys/with-active-issues",
      },
      pending: {
        index: "/new-hire-journeys/pending",
        count: "/new-hire-journeys/pending/count",
      },
      bulkSchedule: {
        prepare: "/new-hire-journeys/:id/bulk-schedule/prepare",
        preview: "/new-hire-journeys/:id/bulk-schedule/preview",
        confirm: "/new-hire-journeys/:id/bulk-schedule/confirm",
        confirmStatus: "/new-hire-journeys/:id/bulk-schedule/confirm-status",
      },
    },
    meetingReservations: {
      show: "/meeting-reservations/:id",
    },
    prebuilt: {
      paths: {
        base: "/prebuilt/paths",
        show: "/prebuilt/paths/:id",
        duplicate: "/prebuilt/paths/:id/duplicate",
        templates: "/prebuilt/paths/:id/templates",
      },
      emailTemplates: {
        show: "/prebuilt/email-templates/:id",
      },
      meetingTemplates: {
        show: "/prebuilt/meeting-templates/:id",
      },
      messageTemplates: {
        show: "/prebuilt/message-templates/:id",
      },
      taskTemplates: {
        show: "/prebuilt/task-templates/:id",
      },
      checkpointTemplates: {
        show: "/prebuilt/checkpoint-templates/:id",
      },
    },
    paths: {
      base: "/paths",
      show: "/paths/:id",
      batchCounts: "/paths/batch-counts",
      newHireJourneys: "/paths/:id/new-hire-journeys",
      addNewHire: "/paths/:id/add-new-hire",
      templates: "/paths/:id/templates",
      addTemplate: "/paths/:id/add-template",
      removeTemplate: "/paths/:id/remove-template",
      // unsure what this is below
      template: "/paths/template",
      defaults: "/paths/defaults",
      duplicate: "/paths/:id/duplicate",

    },
    tasks: {
      base: "/tasks",
      show: "/tasks/:id",
      issues: "/tasks/:id/issues",
      reminders: "/tasks/:id/reminders",
      count: "/tasks/count",
    },
    taskTemplates: {
      base: "/task-templates",
      show: "/task-templates/:id",
      duplicate: "/task-templates/:id/duplicate",
      reminders: "/task-templates/:id/reminders",
    },
    checkpoints: {
      base: "/checkpoints",
      show: "/checkpoints/:id",
    },
    checkpointTemplates: {
      base: "/checkpoint-templates",
      show: "/checkpoint-templates/:id",
      duplicate: "/checkpoint-templates/:id/duplicate",
    },
    taskReminders: {
      base: "/reminders",
      show: "/reminders/:id",
    },
    templates: {
      base: "/templates",
    },
    platformEvents: {
      base: "/platform-events",
      show: "/platform-events/:id",
    },
    mcp: {
      sse: "/mcp/sse",
    },
    mcpTokens: {
      base: "/mcp-tokens",
      show: "/mcp-tokens/:id",
    },
    rolesUsersChannels: "/roles-users-channels",
    calendars: {
      base: "/calendars",
      show: "/calendars/:id",
      permissions: "/calendars/:id/permissions",
    },
  },
});
